/* eslint-disable */
import React, { FC, useEffect, useLayoutEffect, useState } from 'react';
import { Button, Col, Divider, Row, Typography, Spin } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import features from '~/constants/features';
import styled, { css } from 'styled-components';
import { NafathStep } from './NafathModal';
import { AppStoreIcon, GooglePlayIcon, HuaweiIcon, NafathLogoImg } from '~/img';
import {
  CheckCircleOutlined,
  CopyFilled,
  CopyOutlined,
  GoogleCircleFilled,
  QrcodeOutlined,
  LoadingOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useDirection } from '~/hooks/i18nHooks';
import { usePageTitle } from '~/context/PageTitleContext';
import { getTrackingId } from '~/utils/handleGetUtm';
import { useGetApplication } from '~/controllers/wizard';
import Countdown from '~/components/Countdown';
import useCountdown from '~/hooks/useCountdown';

interface NafathModalContentProps {
  currentStep: NafathStep;
  attemptsLeft?: number;
  nafathCooldown?: string;
  retryCooldown?: string;
  nafathCode?: number;
  setIsEnabled?: any;
  onContinue?: () => void;
  loading?: boolean;
}

const NafathModalContent: FC<NafathModalContentProps> = ({
  currentStep,
  nafathCode,
  nafathCooldown,
  retryCooldown,
  attemptsLeft,
  setIsEnabled,
  onContinue,
  loading,
}) => {
  const { t } = useTranslation('ksaApplication');
  const isMobile = useIsMobile();
  const dir = useDirection();
  const { setPageTitle } = usePageTitle();
  const { data }: any = useGetApplication();

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('https://nafid.app/download');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderDownloadLink = () => {
    if (isMobile) {
      return (
        <MobileDownloadSection dir={dir}>
          <Typography.Paragraph className="mobileSectionTitle">
            {t("Download the app here if you don't have it.")}
          </Typography.Paragraph>
          <div className="storeIconsRow">
            <a href="https://apps.apple.com/sa/app/nafath-%D9%86%D9%81%D8%A7%D8%B0/id1598909871" target="_blank" rel="noopener noreferrer">
              <Box>
                <AppStoreIcon style={{ fontSize: 32 }} />
              </Box>
            </a>
            <a href="https://play.google.com/store/apps/details?id=sa.gov.nic.nafath" target="_blank" rel="noopener noreferrer">
              <Box>
                <GooglePlayIcon style={{ fontSize: 32 }} />
              </Box>
            </a>
            <a href="https://appgallery.huawei.com/app/C105996637" target="_blank" rel="noopener noreferrer">
              <Box>
                <HuaweiIcon style={{ fontSize: 32 }} />
              </Box>
            </a>
          </div>
        </MobileDownloadSection>
      );
    }
    return (
      <DownloadLinkSection dir={dir}>
        <Divider style={{ margin: '16px 0 24px' }} />
        <Typography.Paragraph className="sectionTitle">
          {t("Don't have the Nafath app yet?")}
        </Typography.Paragraph>

        <QRCodeWrapper>
          <QRCodeImg src="/img/AppDownloadQRCode.svg" alt={t('Scan to download')} />
          <ScanDownloadText dir={dir}>{t('Scan to download')}</ScanDownloadText>
        </QRCodeWrapper>

        <Typography.Paragraph className="orCopyTitle">
          {t('Or copy the link:')}
        </Typography.Paragraph>

        <CopyLinkContainer dir="ltr">
          <LinkText>https://nafid.app/download</LinkText>
          <CopyButton onClick={handleCopy}>
            <span>{copied ? t('Copied') : t('Copy')}</span>
            {copied ? (
              <CheckCircleOutlined style={{ fontSize: 16 }} />
            ) : (
              <CopyOutlined style={{ fontSize: 16 }} />
            )}
          </CopyButton>
        </CopyLinkContainer>
      </DownloadLinkSection>
    );
  };

  useEffect(() => {
    document.title = t('Nafath Verification');
    setTimeout(() => {
      document.title = t('Nafath Verification');
    }, 500);

    const eventPayload = {
      event: 'event_pageview',
      page_ln: 'en',
      page_title: t('Nafath Verification'),
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      trackingId: getTrackingId(data) || '',
    };
    (window as any).dataLayer.push(eventPayload);
  }, []);

  // Block browser back navigation while the countdown is running
  useEffect(() => {
    const blockedSteps: typeof currentStep[] = [
      'NAFATH_NUMBER',
      'TEMPORARILY_LOCKED',
      'REJECTED',
      'SESSION_EXPIRED',
    ];
    if (!blockedSteps.includes(currentStep)) return;

    // Push a buffer of dummy states so rapid back-clicks can't break through
    const pushBuffer = (count = 10) => {
      for (let i = 0; i < count; i++) {
        history.pushState(null, '', window.location.href);
      }
    };

    pushBuffer();

    const handlePopState = () => {
      // Refill the buffer every time a back-click is detected
      pushBuffer();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentStep]);

  if (loading) {
    return (
      <SpinnerWrapper>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        <LoadingText>{t('Loading...')}</LoadingText>
      </SpinnerWrapper>
    );
  }

  if (currentStep === 'CONFIRMATION') {
    const contractName = data?.payload?.contractGenerated?.name ?? 'Agreement.pdf';
    const contractSize = data?.payload?.contractGenerated?.size
      ? `${Math.round(data.payload.contractGenerated.size / (1024 * 1024))} MB`
      : null;

    return (
      <ConfirmationWrapper dir={dir}>
        {/* file card */}
        <FileCard>
          <FileIconWrap>
            <PdfSvgIcon />
          </FileIconWrap>
          <FileInfo>
            <FileNameText>{contractName}</FileNameText>
            {contractSize && <FileSizeText>{contractSize}</FileSizeText>}
          </FileInfo>
        </FileCard>

        {/* Verify via Nafath button */}
        <Button
          id="nafath_verifyButton"
          type="primary"
          block
          htmlType="submit"
          style={{ height: 48, borderRadius: 10, fontSize: 15, fontWeight: 600, marginTop: 8 }}
        >
          {t('Verify via Nafath')}
        </Button>

        {/* Disclaimer */}
        <DisclaimerText dir={dir}>
          {t('By continuing, you accept to verify your identity through Nafath services.')}
        </DisclaimerText>

        {/* Contact us - mobile only */}
        {isMobile && (
          <ContactLink href={features.contactUsLinkSoftDecline}>{t('Contact us')}</ContactLink>
        )}
      </ConfirmationWrapper>
    );
  }
  if (currentStep === 'NAFATH_NUMBER') {
    return !isMobile ? (
      <Container>
        <div className="nafathSubHeader">
          <NafathLogoImg
            style={{
              width: isMobile ? 60 : 40,
              height: isMobile ? 60 : 40,
              borderRadius: isMobile ? 12 : 8,
            }}
          />

          <Typography.Paragraph className="nafathSubHeaderText">
            {t(
              'Please open the Nafath mobile app and approve the request by selecting the number below.'
            )}
          </Typography.Paragraph>
        </div>

        <NafathCodeSection
          nafathCode={nafathCode}
          expireAt="180"
          onExpire={() => { }}
          onRetry={onContinue}
          loading={loading}
        />

        {renderDownloadLink()}
      </Container>
    ) : (
      <Container>
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div className="nafathSubHeader">
            <NafathLogoImg
              style={{
                width: isMobile ? 60 : 40,
                height: isMobile ? 60 : 40,
                borderRadius: isMobile ? 12 : 8,
              }}
            />
            <Typography.Paragraph className="nafathSubHeaderText">
              {t(
                'Please open the Nafath mobile app and approve the request by selecting the number above.'
              )}
            </Typography.Paragraph>
          </div>

          <NafathCodeSection
            nafathCode={nafathCode}
            expireAt="180"
            onExpire={() => { }}
            onRetry={onContinue}
            loading={loading}
          />

          {renderDownloadLink()}
        </div>
      </Container>
    );
  }

  if (currentStep === 'REJECTED') {
    return (
      <Container>
        {!isMobile && (
          <div className="nafathSubHeader">
            <NafathLogoImg
              style={{
                width: isMobile ? 60 : 40,
                height: isMobile ? 60 : 40,
                borderRadius: isMobile ? 12 : 8,
              }}
            />
            <Typography.Paragraph className="nafathSubHeaderText">
              {t(
                'Please open the Nafath mobile app and approve the request by selecting the number below.'
              )}
            </Typography.Paragraph>
          </div>
        )}
        {!isMobile && <Divider style={{ margin: '16px 0 24px' }} />}

        <NafathRejectedSection
          attemptsLeft={attemptsLeft}
          retryCooldown={"180"}
          onExpire={() => { }}
          onRetry={onContinue}
          loading={loading}
        />

        {renderDownloadLink()}
      </Container>
    );
  }
  if (currentStep === 'TEMPORARILY_LOCKED') {
    return (
      <Container>
        <div className="nafathSubHeader">
          <NafathLogoImg
            style={{
              width: isMobile ? 60 : 40,
              height: isMobile ? 60 : 40,
              borderRadius: isMobile ? 12 : 8,
            }}
          />
          <Typography.Paragraph className="nafathSubHeaderText">
            {t(
              'Please open the Nafath mobile app and approve the request by selecting the number below.'
            )}
          </Typography.Paragraph>
        </div>
        {/* <Divider style={{ margin: '16px 0 24px' }} /> */}

        <NafathLockedSection
          attemptsLeft={attemptsLeft}
          retryCooldown={"180"}
          onExpire={() => { }}
          onRetry={onContinue}
          loading={loading}
        />
        {renderDownloadLink()}
      </Container>
    );
  }
  if (currentStep === 'SESSION_EXPIRED') {
    return (
      <Container>
        <div className="nafathSubHeader">
          <NafathLogoImg
            style={{
              width: isMobile ? 60 : 40,
              height: isMobile ? 60 : 40,
              borderRadius: isMobile ? 12 : 8,
            }}
          />
          <Typography.Paragraph className="nafathSubHeaderText">
            {t(
              'Please open the Nafath mobile app and approve the request by selecting the number below.'
            )}
          </Typography.Paragraph>
        </div>

        <NafathExpiredSection
          nafathCode={nafathCode}
          onRetry={onContinue}
          loading={loading}
        />
        {renderDownloadLink()}
      </Container>
    );
  }
  if (currentStep === 'PERMANENTLY_LOCKED') {
    return (
      <Trans
        t={t}
        i18nKey="The Nafath biometric authentication retry limits have been reached and your account is permanently locked. <1>Contact us</1> to unlock your account."
      >
        {
          'The Nafath biometric authentication retry limits have been reached and your account is permanently locked. '
        }
        <a href={features.contactUsLinkSoftDecline} target="__blank">
          Contact us
        </a>
        {' to unlock your account.'}
      </Trans>
    );
  }
  return (
    <>
      <Typography.Paragraph>{t('Something went wrong :(')}</Typography.Paragraph>
      <Trans t={t} i18nKey="Please <1>Contact us</1> or try again later.">
        {'Please  '}
        <a href={features.contactUsLinkSoftDecline} target="__blank">
          Contact us
        </a>
        {' or try again later.'}
      </Trans>
    </>
  );
};

const Box = styled.div`
  background-color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0px 2px 6px 0px rgba(0, 89, 150, 0.03), 0px -1px 8px -10px rgba(0, 89, 150, 0.03);
`;

/* ── Confirmation step styled components ── */

const ConfirmationWrapper = styled.div<{ dir?: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  direction: ${({ dir }) => dir ?? 'ltr'};
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0px 1px 4px 0px rgba(0, 89, 150, 0.10);
  border: 1px solid #f0f4f8;
`;

const FileIconWrap = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileNameText = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 20px;
`;

const FileSizeText = styled.span`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
  line-height: 18px;
  margin-top: 2px;
`;

const DisclaimerText = styled.p<{ dir?: string }>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #8c8c8c;
  text-align: center;
  line-height: 18px;
  margin: 0;
  padding: 0 8px;
`;

const ContactLink = styled.a`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #0060a8;
  text-align: center;
  margin-top: 4px;
`;

/* Simple inline PDF icon SVG */
const PdfSvgIcon = () => (
  <svg width="36" height="40" viewBox="0 0 36 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="40" rx="6" fill="#FFF1F0" />
    <text x="50%" y="56%" dominantBaseline="middle" textAnchor="middle"
      fill="#E64360" fontSize="10" fontFamily="Inter,sans-serif" fontWeight="700">
      PDF
    </text>
  </svg>
);


export const Container = styled.div`
  width: 100%;
  height: 80%;
  .subTitle2 {
    color: #8f8f8f;
    text-align: center;

    /* FontSize (Body)/Regular */
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 171.429% */
    letter-spacing: 0.14px;
  }
  .subtitle {
    color: #000;
    text-align: center;

    /* FontSize (Body)/Regular */
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 171.429% */
    letter-spacing: 0.14px;
  }
  .linkNafath {
    overflow: hidden;
    color: #005996;
    text-overflow: ellipsis;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.14px;
  }

  .copyLink {
    position: relative;
    border: 1px solid #dadada;
    border-radius: 8px;
    padding: 12px 100px 12px 16px;
    background: #fff;
    min-height: 56px;
    display: flex;
    align-items: center;
  }

  .nafathSubHeader {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 24px;
    gap: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      gap: 24px;
      margin-bottom: 24px;

      .ant-typography {
        text-align: center !important;
        margin: 0 !important;
        padding-top: 0 !important;
      }
    }
  }

  .nafathSubHeaderText {
    margin: 0;
    padding-top: 4px;
    color: var(--Neutral-Color-Text-colorText, #070707);
    /* LG/Medium */
    font-family: var(--Fonts, Inter);
    font-size: var(--Font-Size-Main-Tokens-LG, 16px);
    font-style: normal;
    font-weight: 500;
    line-height: var(--Font-Line-height-lineHeight-LG, 24px); /* 150% */

    [dir="rtl"] & {
      font-family: Tajawal, sans-serif;
    }

    @media (max-width: 768px) {
      padding-top: 0;
      text-align: center;
    }
  }

  .nafathParent {
    text-align: center;
    display: flex;
    justify-content: center;
  }

  .nafathCode {
    width: 100px;
    height: 100px;
    border-radius: 100px;
    background-color: #e6f0f7;
    text-align: center;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    font-weight: 600;
  }
`;


const NafathCodeCard = styled.div`
  display: flex;
  padding: var(--XL-2, 40px);
  flex-direction: column;
  align-items: center;
  gap: var(--XL-2, 40px);
  align-self: stretch;
  border-radius: var(--Normal, 16px);
  background: var(--colorPrimary-colorPrimaryBgHover, #E6F0F7);
  margin-bottom: 24px;

  @media (max-width: 576px) {
    padding: 24px;
    gap: 24px;
  }
`;

const CodeCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #002b49;
  font-family: Inter, sans-serif;
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  box-shadow: 0px 2px 6px 0px rgba(0, 89, 150, 0.03);
`;

const CountdownRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const CountdownText = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: var(--Neutral-Color-Text-colorTextSecondary, #4a4a4a);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CountdownTime = styled.span`
  font-weight: 600;
  color: var(--Neutral-Color-Text-colorTextSecondary, #4a4a4a);
`;

const RetryButton = styled(Button) <{ primary?: boolean }>`
  && {
    height: 48px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 14px;
    width: 100%;
    max-width: 280px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ primary }) =>
    primary
      ? css`
            background-color: #0060a8;
            color: #ffffff;
            box-shadow: 0px 2px 8px rgba(0, 96, 168, 0.15);

            &:hover, &:focus, &:active {
              background-color: #004d87;
              color: #ffffff;
            }
          `
      : css`
            background-color: #ffffff;
            color: #4a4a4a;
            box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);

            &:hover, &:focus, &:active {
              background-color: #f7f9fc;
              color: #333333;
            }
          `}

    &:disabled, &[disabled], &:disabled:hover, &[disabled]:hover {
      background-color: #f5f5f5 !important;
      color: #bfbfbf !important;
      border: none !important;
      box-shadow: none !important;
      cursor: not-allowed;
    }
  }
`;

interface NafathCodeSectionProps {
  nafathCode?: number;
  expireAt: string;
  onExpire?: () => void;
  onRetry?: () => void;
  loading?: boolean;
}

const NafathCodeSection: FC<NafathCodeSectionProps> = ({
  nafathCode,
  expireAt,
  onExpire,
  onRetry,
  loading,
}) => {
  const { t } = useTranslation('ksaApplication');
  const timeLeft = useCountdown(expireAt, onExpire);
  const { minutes, seconds, distance } = timeLeft;

  const formatTime = (time: number) => {
    return time < 10 ? `0${time}` : time;
  };

  const formattedMinutes = formatTime(minutes >= 0 ? minutes : 0);
  const formattedSeconds = formatTime(seconds >= 0 ? seconds : 0);

  const isExpired = distance < 0;

  return (
    <NafathCodeCard>
      <CodeCircle>{nafathCode}</CodeCircle>
      <CountdownRow>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="19"
          height="19"
          viewBox="0 0 19 19"
          fill="none"
          style={{ width: '16.667px', height: '16.667px' }}
        >
          <path
            d="M6.30556 6.30556L9.08333 9.08333M3.19078 3.19078C1.68274 4.69881 0.75 6.78215 0.75 9.08333C0.75 13.6857 4.48096 17.4167 9.08333 17.4167C13.6857 17.4167 17.4167 13.6857 17.4167 9.08333C17.4167 4.48096 13.6857 0.75 9.08333 0.75V3.52778"
            stroke="var(--Neutral-Color-Text-colorTextSecondary, #4a4a4a)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <CountdownText>
          {t('Expires in')}{' '}
          <CountdownTime>{`${formattedMinutes}:${formattedSeconds}`}</CountdownTime>
        </CountdownText>
      </CountdownRow>
      <RetryButton
        id="NafathRetryNewCode"
        onClick={onRetry}
        loading={loading}
        disabled={!isExpired}
        primary={isExpired}
      >
        {t('Retry with a New Code')}
      </RetryButton>
    </NafathCodeCard>
  );
};


const LockedFlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  margin-top: 24px;

  .locked-message {
    font-size: 16px;
    font-weight: 500;
    color: #1b1b20;
    // max-width: 440px;
    margin: 0 !important;
    text-align: center;
    line-height: 24px;

    @media (max-width: 768px) {
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

const QuestionCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #fce1e4;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ba1a1a;
  font-family: Inter, sans-serif;
  font-size: 48px;
  font-weight: 500;
  box-shadow: 0px 2px 6px 0px rgba(186, 26, 26, 0.03);
`;

const AttemptsText = styled.span`
  color: #e64360;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

interface NafathLockedSectionProps {
  attemptsLeft?: number;
  retryCooldown?: string;
  onExpire?: () => void;
  onRetry?: () => void;
  loading?: boolean;
}

const NafathLockedSection: FC<NafathLockedSectionProps> = ({
  attemptsLeft,
  retryCooldown,
  onExpire,
  onRetry,
  loading,
}) => {
  const { t } = useTranslation('ksaApplication');
  const cooldownTarget = retryCooldown || '180';
  const timeLeft = useCountdown(cooldownTarget, onExpire);
  const { minutes, seconds, distance } = timeLeft;

  const formatTime = (time: number) => {
    return time < 10 ? `0${time}` : time;
  };

  const formattedMinutes = formatTime(minutes >= 0 ? minutes : 0);
  const formattedSeconds = formatTime(seconds >= 0 ? seconds : 0);

  const isExpired = distance < 0;

  return (
    <LockedFlexContainer>
      <QuestionCircle>?</QuestionCircle>

      <Typography.Paragraph className="locked-message">
        {t(
          'The Nafath biometric authentication retry is temporarily locked. Please wait before trying again.'
        )}
      </Typography.Paragraph>

      <CountdownRow style={{ marginTop: 8 }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="19"
          height="19"
          viewBox="0 0 19 19"
          fill="none"
          style={{ width: '16.667px', height: '16.667px' }}
        >
          <path
            d="M6.30556 6.30556L9.08333 9.08333M3.19078 3.19078C1.68274 4.69881 0.75 6.78215 0.75 9.08333C0.75 13.6857 4.48096 17.4167 9.08333 17.4167C13.6857 17.4167 17.4167 13.6857 17.4167 9.08333C17.4167 4.48096 13.6857 0.75 9.08333 0.75V3.52778"
            stroke="var(--Neutral-Color-Text-colorTextSecondary, #4a4a4a)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <CountdownText>
          {t('Try again in')}{' '}
          <CountdownTime>{`${formattedMinutes}:${formattedSeconds}`}</CountdownTime>
        </CountdownText>
      </CountdownRow>

      <RetryButton
        id="NafathRetryNewCodeLocked"
        onClick={onRetry}
        loading={loading}
        disabled={!isExpired}
        style={{ marginTop: 8 }}
        primary={isExpired}
      >
        {t('Retry with a New Code')}
      </RetryButton>

      {attemptsLeft !== undefined && (
        <AttemptsText style={{ marginTop: 8 }}>
          {t('You have {{count}} attempts left.', { count: attemptsLeft })}
        </AttemptsText>
      )}
    </LockedFlexContainer>
  );
};


interface NafathRejectedSectionProps {
  attemptsLeft?: number;
  retryCooldown?: string;
  onExpire?: () => void;
  onRetry?: () => void;
  loading?: boolean;
}

const NafathRejectedSection: FC<NafathRejectedSectionProps> = ({
  attemptsLeft,
  retryCooldown,
  onExpire,
  onRetry,
  loading,
}) => {
  const { t } = useTranslation('ksaApplication');
  const cooldownTarget = retryCooldown || '180';
  const timeLeft = useCountdown(cooldownTarget, onExpire);
  const { minutes, seconds, distance } = timeLeft;

  const formatTime = (time: number) => {
    return time < 10 ? `0${time}` : time;
  };

  const formattedMinutes = formatTime(minutes >= 0 ? minutes : 0);
  const formattedSeconds = formatTime(seconds >= 0 ? seconds : 0);

  const isExpired = distance < 0;

  return (
    <LockedFlexContainer>
      <QuestionCircle>
        <CloseOutlined />
      </QuestionCircle>

      <Typography.Paragraph className="locked-message">
        {t(
          'The Nafath biometric authentication request was rejected.'
        )}
      </Typography.Paragraph>

      {!isExpired && (
        <CountdownRow style={{ marginTop: 8 }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            style={{ width: '16.667px', height: '16.667px' }}
          >
            <path
              d="M6.30556 6.30556L9.08333 9.08333M3.19078 3.19078C1.68274 4.69881 0.75 6.78215 0.75 9.08333C0.75 13.6857 4.48096 17.4167 9.08333 17.4167C13.6857 17.4167 17.4167 13.6857 17.4167 9.08333C17.4167 4.48096 13.6857 0.75 9.08333 0.75V3.52778"
              stroke="var(--Neutral-Color-Text-colorTextSecondary, #4a4a4a)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <CountdownText>
            {t('Try again to complete the authentication in')}{' '}
            <CountdownTime>{`${formattedMinutes}:${formattedSeconds}`}</CountdownTime>
          </CountdownText>
        </CountdownRow>
      )}

      <RetryButton
        id="NafathRetryNewCodeRejected"
        onClick={onRetry}
        loading={loading}
        disabled={!isExpired}
        style={{ marginTop: 8 }}
        primary={isExpired}
      >
        {t('Retry with a New Code')}
      </RetryButton>

      {attemptsLeft !== undefined && (
        <AttemptsText style={{ marginTop: 8 }}>
          {t('You have {{count}} attempts left.', { count: attemptsLeft })}
        </AttemptsText>
      )}
    </LockedFlexContainer>
  );
};


const ExpiredCodeCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #fce1e4;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ba1a1a;
  font-family: Inter, sans-serif;
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  box-shadow: 0px 2px 6px 0px rgba(186, 26, 26, 0.03);
`;

interface NafathExpiredSectionProps {
  nafathCode?: number;
  onRetry?: () => void;
  loading?: boolean;
}

const NafathExpiredSection: FC<NafathExpiredSectionProps> = ({
  nafathCode,
  onRetry,
  loading,
}) => {
  const { t } = useTranslation('ksaApplication');

  return (
    <NafathCodeCard>
      <ExpiredCodeCircle>{nafathCode}</ExpiredCodeCircle>

      <Typography.Paragraph
        style={{
          fontSize: 14,
          fontWeight: 400,
          color: '#1b1b20',
          margin: 0,
          textAlign: 'center',
        }}
      >
        {t('Authentication request expired')}
      </Typography.Paragraph>

      <RetryButton
        id="NafathRetryNewCodeExpired"
        onClick={onRetry}
        loading={loading}
        primary
      >
        {t('Retry with a New Code')}
      </RetryButton>
    </NafathCodeCard>
  );
};

const DownloadLinkSection = styled.div<{ dir?: string }>`
  margin-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  // align-items: ${({ dir }) => (dir === 'rtl' ? 'flex-end' : 'flex-start')};
  align-items:flex-start;
  text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};

  .sectionTitle {
    && {
      font-size: 14px;
      font-weight: 500;
      color: #1b1b20;
      margin-bottom: 16px;
      width: 100%;
    }
  }

  .orCopyTitle {
    && {
      font-size: 14px;
      font-weight: 500;
      color: #7b7b80;
      margin-top: 16px;
      margin-bottom: 8px;
      width: 100%;
    }
  }
`;

const QRCodeWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const QRCodeImg = styled.img`
  width: 164px;
  height: 164px;
  object-fit: contain;
`;

const ScanDownloadText = styled.span<{ dir?: string }>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #7b7b80;
  margin-top: 12px;
  text-align: center;
`;

const CopyLinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #dcdce0;
  border-radius: 12px;
  padding: 6px 6px 6px 16px;
  background: #ffffff;
  min-height: 48px;
  width: 100%;
`;

const LinkText = styled.span`
  color: #005996;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #0069B1;
  color: #ffffff;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #004c80;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const MobileDownloadSection = styled.div<{ dir?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 16px;

  .mobileSectionTitle {
    && {
      color: #8f8f8f;
      text-align: center;
      font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.14px;
      margin-bottom: 16px;
    }
  }

  .storeIconsRow {
    display: flex;
    gap: 24px;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  a {
    display: inline-block;
    color: inherit;
    text-decoration: none;
    transition: transform 0.2s ease;
    
    &:hover, &:active {
      transform: scale(1.05);
    }
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  min-height: 280px;
  width: 100%;
`;

const LoadingText = styled.p`
  margin: 0;
  font-family: Inter, Tajawal, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #8c8c8c;
  text-align: center;
`;

export default NafathModalContent;
